import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Send, Loader2, Users, MessageSquare } from "lucide-react";
import { toast } from "sonner";

const AUDIENCES = [
  { id: "painters", label: "All painters" },
  { id: "customers", label: "All customers" },
  { id: "all", label: "Everyone" },
];

const AdminBroadcast = () => {
  const [templates, setTemplates] = useState({});
  const [key, setKey] = useState("broadcast");
  const [audience, setAudience] = useState("painters");
  const [vars, setVars] = useState({});
  const [counting, setCounting] = useState(false);
  const [sending, setSending] = useState(false);
  const [recipients, setRecipients] = useState(null);
  const [history, setHistory] = useState([]);
  const [templatesError, setTemplatesError] = useState("");

  useEffect(() => {
    api.get("/admin/templates")
      .then((r) => { setTemplates(r.data.templates); setTemplatesError(""); })
      .catch(() => {
        setTemplatesError("Couldn't load templates. Please hard-refresh (Cmd/Ctrl+Shift+R) — the deploy may still be rolling out.");
        toast.error("Couldn't load templates");
      });
    api.get("/admin/broadcasts").then((r) => setHistory(r.data.items)).catch(() => {});
  }, []);

  useEffect(() => { setVars({}); setRecipients(null); }, [key]);

  const tpl = templates[key];

  const dryRun = async () => {
    setCounting(true);
    try {
      const res = await api.post("/admin/broadcast", { template_key: key, audience, variables: vars, dry_run: true });
      setRecipients(res.data.recipients);
    } catch { toast.error("Couldn't count"); }
    finally { setCounting(false); }
  };

  const send = async () => {
    if (!tpl?.vars?.every((v) => (vars[v] || "").trim())) {
      toast.error("Please fill every variable");
      return;
    }
    if (!window.confirm(`Send to ${recipients ?? "all"} recipients now?`)) return;
    setSending(true);
    try {
      const res = await api.post("/admin/broadcast", { template_key: key, audience, variables: vars });
      toast.success(`Sent: ${res.data.sent}  ·  Failed: ${res.data.failed}`);
      const h = await api.get("/admin/broadcasts");
      setHistory(h.data.items);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  return (
    <div>
      <h1 className="font-heading text-3xl font-bold text-[#2D3748]">Broadcast</h1>
      <p className="text-[#718096] mt-1">Send a Meta-approved WhatsApp template to a cohort. Use <code className="bg-[#F7FAFC] px-1.5 py-0.5 rounded text-xs">{`{first_name}`}</code> in any field to personalise.</p>

      <div className="mt-8 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-[#E2E8F0] rounded-2xl p-6 space-y-5">
          <div>
            <Label className="text-[#2D3748] font-heading font-medium">Template</Label>
            <select
              data-testid="bc-template"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              className="mt-1.5 w-full rounded-xl border border-[#E2E8F0] px-3 py-2.5 bg-white"
            >
              {Object.entries(templates).map(([k, t]) => (
                <option key={k} value={k} disabled={!t.configured}>{t.name}{t.configured ? "" : " · not configured"}</option>
              ))}
            </select>
            {Object.keys(templates).length === 0 && (
              <p data-testid="bc-templates-error" className="mt-2 text-sm text-[#DC2626]">
                {templatesError || "Loading templates…"}
              </p>
            )}
          </div>

          {tpl && (
            <>
              <div>
                <Label className="text-[#2D3748] font-heading font-medium">Audience</Label>
                <div className="mt-1.5 inline-flex bg-[#F7FAFC] border border-[#E2E8F0] rounded-full p-1">
                  {AUDIENCES.map((a) => (
                    <button
                      key={a.id}
                      onClick={() => { setAudience(a.id); setRecipients(null); }}
                      data-testid={`bc-aud-${a.id}`}
                      className={`px-4 py-1.5 rounded-full text-sm font-heading font-medium ${audience === a.id ? "bg-white shadow-sm text-[#2D3748]" : "text-[#718096]"}`}
                    >{a.label}</button>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                {(tpl.vars || []).map((name, i) => (
                  <div key={name}>
                    <Label className="text-[#2D3748] font-heading font-medium capitalize">{name.replace("_", " ")} <span className="text-xs text-[#718096]">{`{{${i + 1}}}`}</span></Label>
                    {name === "message" ? (
                      <textarea
                        data-testid={`bc-var-${name}`}
                        value={vars[name] || ""}
                        onChange={(e) => setVars({ ...vars, [name]: e.target.value })}
                        rows={3}
                        className="mt-1.5 w-full rounded-xl border border-[#E2E8F0] px-3 py-2.5 outline-none focus:border-[#25D366]"
                        placeholder="Three new customer leads landed in Durbanville this week."
                      />
                    ) : (
                      <Input data-testid={`bc-var-${name}`} value={vars[name] || ""} onChange={(e) => setVars({ ...vars, [name]: e.target.value })} className="mt-1.5 rounded-xl" placeholder={name === "first_name" ? "{first_name}  (auto-personalised)" : ""} />
                    )}
                  </div>
                ))}
              </div>

              <div className="bg-[#F7FAFC] border border-[#E2E8F0] rounded-xl p-4 text-sm whitespace-pre-line text-[#2D3748]" data-testid="bc-preview">
                <div className="text-xs text-[#718096] font-heading font-semibold uppercase mb-2">Preview</div>
                {tpl.preview.replace(/\{\{(\d+)\}\}/g, (_, n) => vars[tpl.vars[Number(n) - 1]] || `{{${n}}}`)}
              </div>

              <div className="flex flex-wrap items-center gap-3 pt-2">
                <Button onClick={dryRun} variant="outline" disabled={counting} data-testid="bc-count" className="rounded-full">
                  {counting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Users className="w-4 h-4 mr-2" />}
                  {recipients !== null ? `${recipients} recipients` : "Count recipients"}
                </Button>
                <Button onClick={send} disabled={sending} data-testid="bc-send" className="rounded-full bg-[#25D366] hover:bg-[#16A34A] text-white">
                  {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />} Send broadcast
                </Button>
              </div>
            </>
          )}
        </div>

        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5">
          <div className="flex items-center gap-2 font-heading font-semibold text-[#2D3748]">
            <MessageSquare className="w-4 h-4 text-[#1E88E5]" /> Recent broadcasts
          </div>
          {history.length === 0 ? (
            <p className="mt-3 text-sm text-[#718096]">No broadcasts yet.</p>
          ) : (
            <ul className="mt-3 space-y-3">
              {history.slice(0, 10).map((b, i) => (
                <li key={i} className="text-xs text-[#2D3748] border-b border-[#E2E8F0] pb-2 last:border-0">
                  <div className="font-medium">{b.template_key} · {b.audience}</div>
                  <div className="text-[#718096]">{b.sent}/{b.recipients_total} sent · {new Date(b.created_at).toLocaleString()}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminBroadcast;
